import Phaser from 'phaser';
import { requestPasswordReset, signIn, signUp } from '@/systems/AuthService';
import { addBackdrop } from '@/ui/background';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

type Mode = 'signin' | 'signup' | 'reset';

/**
 * AuthScene — porta de entrada do jogo enquanto não há sessão Supabase.
 *
 * Renderiza um formulário HTML (via scene.add.dom) porque inputs reais
 * dão suporte nativo a autocomplete, gerenciador de senhas, teclado
 * mobile com tipos certos (email/password) e acessibilidade. Reescrever
 * isso em canvas seria muito trabalho pra zero ganho.
 *
 * Três modos compartilham o mesmo container — toggle troca o `innerHTML`
 * do form e re-vincula handlers. Não precisa de framework: a cena é
 * curta e a complexidade está controlada.
 */
export class AuthScene extends Phaser.Scene {
  private mode: Mode = 'signin';
  private formDom?: Phaser.GameObjects.DOMElement;
  private statusDom?: Phaser.GameObjects.DOMElement;
  private busy = false;

  constructor() {
    super({ key: 'AuthScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, 88, 'Tabuada Geek', {
        fontFamily: Typography.fontFamily,
        fontSize: '64px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 144, 'Acesse com sua conta de responsável', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.formDom = this.add.dom(width / 2, height / 2 + 40).createFromHTML(this.buildFormHtml());
    this.statusDom = this.add.dom(width / 2, height - 80).createFromHTML(`<div id="auth-status" style="${statusStyle}"></div>`);

    this.bindHandlers();
  }

  private buildFormHtml(): string {
    return `
      <div style="${cardStyle}">
        <div style="${tabsStyle}">
          ${this.tabHtml('signin', 'Entrar')}
          ${this.tabHtml('signup', 'Criar conta')}
          ${this.tabHtml('reset', 'Esqueci a senha')}
        </div>
        <form id="auth-form" autocomplete="on" style="${formStyle}">
          ${this.modeFieldsHtml()}
          <button type="submit" id="auth-submit" style="${submitStyle}">${this.submitLabel()}</button>
        </form>
      </div>
    `;
  }

  private modeFieldsHtml(): string {
    if (this.mode === 'signup') {
      return `
        <div style="${rowStyle}">
          ${input('first_name', 'Nome do responsável', 'text', 'given-name')}
          ${input('last_name', 'Sobrenome', 'text', 'family-name')}
        </div>
        ${input('email', 'E-mail', 'email', 'email')}
        ${input('password', 'Senha (mín. 6 caracteres)', 'password', 'new-password')}
        <label style="${consentStyle}">
          <input type="checkbox" id="auth-consent" />
          <span>
            Sou responsável pelo aluno e autorizo o cadastro conforme a
            <a href="#" id="auth-terms" style="color:${Palette.accentCss}">política de privacidade</a>.
          </span>
        </label>
      `;
    }
    if (this.mode === 'reset') {
      return `
        <p style="${helperStyle}">
          Informe o e-mail cadastrado e enviaremos um link pra criar uma senha nova.
        </p>
        ${input('email', 'E-mail', 'email', 'email')}
      `;
    }
    return `
      ${input('email', 'E-mail', 'email', 'email')}
      ${input('password', 'Senha', 'password', 'current-password')}
    `;
  }

  private tabHtml(tab: Mode, label: string): string {
    const active = this.mode === tab;
    const bg = active ? Palette.primaryCss : 'transparent';
    const color = active ? Palette.textPrimaryCss : Palette.textMutedCss;
    return `<button type="button" class="auth-tab" data-tab="${tab}" style="${tabStyle(bg, color)}">${label}</button>`;
  }

  private submitLabel(): string {
    if (this.busy) return 'Aguarde...';
    if (this.mode === 'signup') return 'Criar conta';
    if (this.mode === 'reset') return 'Enviar link';
    return 'Entrar';
  }

  private bindHandlers(): void {
    const root = this.formDom!.node as HTMLElement;
    const tabs = root.querySelectorAll<HTMLButtonElement>('.auth-tab');
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        if (this.busy) return;
        this.mode = tab.dataset.tab as Mode;
        this.refresh();
      });
    });

    const form = root.querySelector<HTMLFormElement>('#auth-form');
    form?.addEventListener('submit', (ev) => {
      ev.preventDefault();
      void this.handleSubmit();
    });

    const terms = root.querySelector<HTMLAnchorElement>('#auth-terms');
    terms?.addEventListener('click', (ev) => {
      ev.preventDefault();
      this.setStatus('Política em /docs (a publicar) — em dev pode marcar e seguir.', 'muted');
    });
  }

  private async handleSubmit(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.refreshSubmitLabel();
    this.setStatus('', 'muted');

    const root = this.formDom!.node as HTMLElement;
    const get = (id: string) => (root.querySelector<HTMLInputElement>(`#${id}`)?.value ?? '').trim();

    try {
      if (this.mode === 'signin') {
        const result = await signIn({ email: get('email'), password: get('password') });
        if (!result.ok) return this.setStatus(result.error, 'error');
        this.setStatus('Bem-vindo!', 'success');
        fadeToScene(this, 'ProfileSelectScene');
      } else if (this.mode === 'signup') {
        const consent = root.querySelector<HTMLInputElement>('#auth-consent')?.checked ?? false;
        const result = await signUp({
          firstName: get('first_name'),
          lastName: get('last_name'),
          email: get('email'),
          password: get('password'),
          consent,
        });
        if (!result.ok) return this.setStatus(result.error, 'error');
        if (result.value.session) {
          this.setStatus('Conta criada — entrando...', 'success');
          fadeToScene(this, 'ProfileSelectScene');
        } else {
          this.setStatus(
            'Conta criada! Confirme o e-mail antes de entrar (verifique sua caixa de entrada).',
            'success',
          );
          this.mode = 'signin';
          this.refresh();
        }
      } else {
        const result = await requestPasswordReset(get('email'));
        if (!result.ok) return this.setStatus(result.error, 'error');
        this.setStatus('Link enviado — confira seu e-mail.', 'success');
      }
    } finally {
      this.busy = false;
      this.refreshSubmitLabel();
    }
  }

  private refresh(): void {
    if (!this.formDom) return;
    this.formDom.setHTML(this.buildFormHtml());
    this.bindHandlers();
  }

  private refreshSubmitLabel(): void {
    const btn = (this.formDom?.node as HTMLElement | undefined)?.querySelector<HTMLButtonElement>('#auth-submit');
    if (btn) btn.textContent = this.submitLabel();
    if (btn) btn.disabled = this.busy;
  }

  private setStatus(message: string, kind: 'muted' | 'success' | 'error'): void {
    const el = (this.statusDom?.node as HTMLElement | undefined)?.querySelector<HTMLDivElement>('#auth-status');
    if (!el) return;
    const colorMap = {
      muted: Palette.textMutedCss,
      success: Palette.successCss,
      error: Palette.dangerCss,
    } as const;
    el.style.color = colorMap[kind];
    el.textContent = message;
  }
}

/* ---------- estilos inline (mantêm a cena autocontida) ---------- */

const cardStyle = `
  width: 460px; padding: 28px 32px;
  background: rgba(20, 20, 40, 0.92);
  border: 1px solid ${Palette.textDimCss}55;
  border-radius: 16px;
  font-family: ${Typography.fontFamily};
  color: ${Palette.textPrimaryCss};
  box-shadow: 0 12px 40px rgba(0,0,0,0.45);
`;

const tabsStyle = `
  display: flex; gap: 8px; margin-bottom: 20px;
`;

const tabStyle = (bg: string, color: string) => `
  flex: 1; padding: 10px 8px; min-height: 44px;
  background: ${bg}; color: ${color};
  border: 1px solid ${Palette.textDimCss}55; border-radius: 10px;
  font-size: 13px; font-weight: 600; cursor: pointer;
  font-family: ${Typography.fontFamily};
`;

const formStyle = `
  display: flex; flex-direction: column; gap: 12px;
`;

const rowStyle = `
  display: flex; gap: 12px;
`;

const inputStyle = `
  flex: 1; padding: 12px 14px; min-height: 44px;
  background: rgba(0,0,0,0.35);
  border: 1px solid ${Palette.textDimCss}66; border-radius: 10px;
  color: ${Palette.textPrimaryCss};
  font-size: 16px; font-family: ${Typography.fontFamily};
  outline: none;
`;

const submitStyle = `
  margin-top: 6px; padding: 14px; min-height: 48px;
  background: ${Palette.primaryCss}; color: ${Palette.textPrimaryCss};
  border: none; border-radius: 12px;
  font-size: 18px; font-weight: 700; cursor: pointer;
  font-family: ${Typography.fontFamily};
`;

const consentStyle = `
  display: flex; gap: 10px; align-items: flex-start;
  font-size: 13px; color: ${Palette.textMutedCss}; line-height: 1.4;
`;

const helperStyle = `
  margin: 0 0 4px 0; font-size: 14px; color: ${Palette.textMutedCss};
`;

const statusStyle = `
  font-family: ${Typography.fontFamily};
  font-size: 16px; font-weight: 600;
  text-align: center; min-height: 24px;
`;

function input(id: string, placeholder: string, type: string, autocomplete: string): string {
  return `<input id="${id}" name="${id}" type="${type}" placeholder="${placeholder}" autocomplete="${autocomplete}" required style="${inputStyle}" />`;
}
